import type React from 'react';
import { AdornedInput, FormField, FormHelp, Label } from '@zillow/constellation-10';

import PropertyType_v1 from './PropertyType_v1';
import type { Section } from '../../../models/index';
import { PROPERTY_TYPE } from '../../../constants/index';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: Section) => void;
};

// Characters to block in number input (we only want positive integers)
const BLOCKED_KEYS = ['-', '+', '.', 'e', 'E'];

function PropertyType_v4({ section, setSectionValues, setSection }: Props): JSX.Element {
  function setNumberOfUnits(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({ [PROPERTY_TYPE.FIELD_NAME.NUMBER_OF_UNITS]: e.currentTarget.value });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (BLOCKED_KEYS.includes(e.key)) {
      e.preventDefault();
    }
  }

  const isMultiFamily = section.getFieldValue(PROPERTY_TYPE.FIELD_NAME.TYPE_MULTI_FAMILY);
  const numberOfUnits = section.getFieldValue(PROPERTY_TYPE.FIELD_NAME.NUMBER_OF_UNITS);
  const numValue = Number(numberOfUnits);
  const hasValue = numberOfUnits !== '' && numberOfUnits != null;
  const isValidNumber = !isNaN(numValue) && Number.isInteger(numValue);
  const isAtLeastTwo = numValue >= 2;
  const hasError = hasValue && (!isValidNumber || !isAtLeastTwo);

  function getErrorMessage(): string {
    if (!isValidNumber) {
      return 'Enter a whole number';
    }
    if (!isAtLeastTwo) {
      return 'Multifamily properties require at least 2 units';
    }
    return '';
  }

  return (
    <PropertyType_v1 {...{ section, setSectionValues, setSection }}>
      {isMultiFamily && (
        <FormField
          required
          error={hasError}
          label={<Label>How many units does the property have?</Label>}
          control={
            <AdornedInput.Root error={hasError}>
              <AdornedInput.Input
                type="number"
                min={2}
                step={1}
                value={numberOfUnits}
                onChange={setNumberOfUnits}
                onKeyDown={onKeyDown}
              />
              <AdornedInput.Adornment>units</AdornedInput.Adornment>
            </AdornedInput.Root>
          }
          description={hasError && <FormHelp>{getErrorMessage()}</FormHelp>}
        />
      )}
    </PropertyType_v1>
  );
}

export default PropertyType_v4;

PropertyType_v4.description = 'Asks for number of units';
